'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AppBar, Box, Toolbar } from '@mui/material';

// Deep imports, not the '@/components' barrel: MainLayout wraps every route, so
// a barrel import here drags the whole component graph (charts included) into
// every page's chunk set.
import Footer from '@/components/layout/Footer';
import { AiChatWidget } from '@/components/specific/ai-support/AiChatWidgetDynamic';
import GuestClickGuard from '@/components/specific/auth/GuestClickGuard';
import { ProxyWalletInitializer } from '@/components/specific/auth/ProxyWalletInitializer';
import { MobileMenuDrawer } from '@/components/specific/drawers/MobileMenuDrawer';
import { GeoBlockBanner } from '@/components/specific/GeoBlockBanner/GeoBlockBanner';
import { AppHeader } from '@/components/specific/header/AppHeader';
import { ModalsOrchestrator } from '@/components/specific/layout/ModalsOrchestrator';
import { BottomNavigation } from '@/components/specific/nav/BottomNavigation';
import { CategoryNavLinks } from '@/components/specific/nav/CategoryNavLinks';
import { OnboardingGate } from '@/components/specific/onboarding';
import { PolymarketApiStatusBanner } from '@/components/specific/PolymarketApiStatusBanner/PolymarketApiStatusBanner';
import { StreakBanner } from '@/components/specific/StreakBanner/StreakBanner';
import { useTheme as useAppTheme } from '@/contexts/ThemeContext';
import { useAuth, useSyncBookmarks } from '@/hooks';
import { useLayoutMobileState, useLayoutModals, useLayoutSearch } from '@/hooks/layout';
import { useGuestGate } from '@/hooks/useGuestGate';
import { usePortfolioSummary } from '@/services/query';
import { useAppDispatch } from '@/store';
import { setSetupRequested } from '@/store/slices/auth.slice';

import { LAYOUT_MAX_WIDTH, PAGE_HORIZONTAL_PADDING } from '@/constants';

interface MainLayoutProps {
  children: React.ReactNode;
}

/**
 * MainLayout - Primary application layout wrapper.
 */
export const MainLayout = ({ children }: MainLayoutProps) => {
  const { mode, toggleTheme } = useAppTheme();

  const { user, isAuthenticated, isLoading } = useAuth();
  useSyncBookmarks(isAuthenticated);
  const {
    data: portfolioSummary,
    isLoading: isQueryLoading,
    effectiveProxy,
  } = usePortfolioSummary();

  const modals = useLayoutModals();

  const search = useLayoutSearch();
  const { mobileMenuDrawer } = useLayoutMobileState();

  const searchInputRef = useRef<HTMLDivElement>(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isInitialDelay, setIsInitialDelay] = useState(true);
  const isLoadingPortfolio =
    isInitialDelay ||
    isQueryLoading ||
    portfolioSummary === undefined ||
    !effectiveProxy ||
    (portfolioSummary as any).isPlaceholder;

  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  const router = useRouter();

  // Signed-out users may only view the landing page. `isBlockedRoute` is true when
  // a guest is on any other route; the effect below sends them home and opens login.
  const { isBlockedRoute } = useGuestGate();

  useEffect(() => {
    if (!isBlockedRoute) return;
    modals.auth.open();
    router.replace('/');
    // Only the trigger matters here; re-running on modals/router identity changes
    // would just re-fire an idempotent redirect.
  }, [isBlockedRoute]);

  const handleLoginClick = () => {
    modals.auth.open();
  };

  const handleDepositClick = () => {
    if (!isAuthenticated) {
      modals.auth.open();
      return;
    }
    if (!user?.isReferredUser) {
      modals.referral.open();
      return;
    }
    dispatch(setSetupRequested(true));
    modals.deposit.open();
  };

  const handleMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsMoreOpen((prev) => !prev);
  };

  const handleMoreClose = () => {
    setIsMoreOpen(false);
  };

  // Small fixed safety margin, not a real loading requirement — usePortfolioSummary's
  // isPlaceholder/effectiveProxy checks already correctly represent "not ready yet",
  // and its query resolves all balance sources atomically via Promise.all, so there's
  // no genuine partial/incorrect-value state this needs to cover. This used to be a
  // flat 3000ms (no data dependency, just an unconditional delay on every page load
  // for every user) — cut to a token amount in case there was ever a real reason for
  // it, without reintroducing a multi-second artificial wait on every navigation.
  useEffect(() => {
    const timer = setTimeout(() => setIsInitialDelay(false), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <ProxyWalletInitializer />
      {/* Signed-out welcome splash. Sits above the app chrome but below the auth
          modal (ModalsOrchestrator), so "Get started" opens login over it. */}
      <OnboardingGate />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          transition: 'background-color 0.3s ease, color 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            width: '100%',
            bgcolor: 'background.default',
          }}
        >
          <AppBar
            position="static"
            elevation={0}
            sx={{
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              backdropFilter: 'blur(8px)',
              width: '100%',
            }}
          >
            <PolymarketApiStatusBanner />
            <GeoBlockBanner />
            <StreakBanner />
            <Toolbar sx={{ p: 0, width: '100%' }}>
              <AppHeader
                searchInputRef={searchInputRef}
                isAuthenticated={isAuthenticated}
                isLoading={isLoading}
                portfolioValue={portfolioSummary?.totalValue || 0}
                cashBalance={portfolioSummary?.cashBalance || 0}
                isLoadingPortfolio={isLoadingPortfolio}
                onSearchClick={search.onSearchClick}
                searchQuery={search.searchQuery}
                onSearchChange={search.onSearchChange}
                onSearchSubmit={search.onSearchSubmit}
                onHowItWorksOpen={modals.howItWorks.open}
                onLoginClick={handleLoginClick}
                onDepositModalOpen={handleDepositClick}
                onReferralModalOpen={modals.referral.open}
                isReferredUser={user?.isReferredUser}
                isAuthSyncing={false}
              />
            </Toolbar>
          </AppBar>
          <GuestClickGuard>
            <CategoryNavLinks
              isMoreOpen={isMoreOpen}
              moreButtonRef={moreButtonRef}
              onMoreClick={handleMoreClick}
              onMoreClose={handleMoreClose}
            />
          </GuestClickGuard>
        </Box>
        <Box
          component="main"
          sx={{
            width: '100%',
            maxWidth: LAYOUT_MAX_WIDTH,
            mx: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            px: PAGE_HORIZONTAL_PADDING,
          }}
        >
          {/* Guests only ever see the landing page. On a blocked route the effect
              above is redirecting home, so render nothing to avoid flashing gated
              content. On allowed pages, the guard turns interactive clicks into a
              login prompt. */}
          {isBlockedRoute ? null : <GuestClickGuard>{children}</GuestClickGuard>}
        </Box>
        <Footer onHowItWorksOpen={modals.howItWorks.open} isLoading={isLoading} />
        <MobileMenuDrawer
          isOpen={mobileMenuDrawer.isOpen}
          onClose={mobileMenuDrawer.close}
          isDarkMode={mode === 'dark'}
          onThemeToggle={toggleTheme}
        />
        <ModalsOrchestrator
          auth={{
            isOpen: modals.auth.isOpen,
            onClose: modals.auth.onClose,
            locked: modals.auth.locked,
          }}
          deposit={{ isOpen: modals.deposit.isOpen, onClose: modals.deposit.onClose }}
          howItWorks={{ isOpen: modals.howItWorks.isOpen, onClose: modals.howItWorks.onClose }}
          search={{
            isOpen: search.isDropdownOpen,
            onClose: search.onDropdownClose,
            anchorRef: searchInputRef,
            searchQuery: search.searchQuery,
          }}
          referral={{ isOpen: modals.referral.isOpen, onClose: modals.referral.onClose }}
        />
        <BottomNavigation
          onMenuClick={mobileMenuDrawer.toggle}
          onDepositClick={handleDepositClick}
          isLoading={isLoading}
        />
      </Box>
      {/* AI support chat. The component already returns null for guests, but it is
          next/dynamic — rendering it unconditionally still downloaded its chunk on
          every page for every visitor, only to render nothing. Hoisting the same
          isAuthenticated check to the call site keeps rendering identical and skips
          the download entirely for signed-out users. */}
      {isAuthenticated && <AiChatWidget />}
    </>
  );
};

export default MainLayout;
